import request, { generateNotyMessage } from 'utils/request'
import { actions as notyActions } from 'layouts/ErrorBox'
import { browserHistory } from 'utils/navigation'
import { INSTRUMENT_TYPE_MAP } from 'utils/constants'
import { getStudyVersionID } from 'utils/studyConfigUtils'
import { loadingActions } from 'store/loader'
import { _fetchParticipants } from '../../../../Participants/routes/ParticipantsPage/modules/Participants'
import { ACTION_LABELS } from '../../DataCorrectionV2Page/modules/DataCorrectionV2'
import {
  buildSurveyQuestionMap,
  formatSurveyQuestions,
  withSurveyQuestionMeta,
} from '../../../utils/surveyQuestions'
import getInstrumentDisplayName from '../../../utils/instrumentName'

const RESET_CREATE_DCF = 'RESET_CREATE_DCF'
const SET_DCF_PARTICIPANTS = 'SET_DCF_PARTICIPANTS'
const SET_DCF_INSTRUMENTS = 'SET_DCF_INSTRUMENTS'
const SET_DCF_INSTANCES = 'SET_DCF_INSTANCES'
const SET_DCF_ANSWERS = 'SET_DCF_ANSWERS'
const SET_CREATE_DCF_STEP = 'SET_CREATE_DCF_STEP'
const SET_SELECTED_PARTICIPANT = 'SET_SELECTED_PARTICIPANT'
const SET_SELECTED_INSTRUMENT = 'SET_SELECTED_INSTRUMENT'
const SET_SELECTED_INSTANCE = 'SET_SELECTED_INSTANCE'
const SET_SELECTED_ACTION = 'SET_SELECTED_ACTION'
const SET_INSTRUMENT_VERSION = 'SET_INSTRUMENT_VERSION'
const SET_DCF_DATES = 'SET_DCF_DATES'
const UPDATE_DCF_ANSWER = 'UPDATE_DCF_ANSWER'
const ADD_DCF_ANSWER_ROW = 'ADD_DCF_ANSWER_ROW'
const REMOVE_DCF_ANSWER_ROW = 'REMOVE_DCF_ANSWER_ROW'

export const DCF_ACTION_OPTIONS = Object.keys(ACTION_LABELS).map(code => ({
  key: code,
  text: ACTION_LABELS[code],
}))

const resetCreateDcf = () => ({ type: RESET_CREATE_DCF })

const setParticipants = list => ({
  type: SET_DCF_PARTICIPANTS,
  payload: list,
})

const setInstruments = list => ({
  type: SET_DCF_INSTRUMENTS,
  payload: list,
})

const setInstances = list => ({
  type: SET_DCF_INSTANCES,
  payload: list,
})

const setAnswers = list => ({
  type: SET_DCF_ANSWERS,
  payload: list,
})

const setStep = step => ({
  type: SET_CREATE_DCF_STEP,
  payload: step,
})

const setSelectedParticipant = participant => ({
  type: SET_SELECTED_PARTICIPANT,
  payload: participant,
})

const setSelectedInstrument = instrument => ({
  type: SET_SELECTED_INSTRUMENT,
  payload: instrument,
})

const setSelectedInstance = instance => ({
  type: SET_SELECTED_INSTANCE,
  payload: instance,
})

const setSelectedAction = action => ({
  type: SET_SELECTED_ACTION,
  payload: action,
})

const setInstrumentVersion = version => ({
  type: SET_INSTRUMENT_VERSION,
  payload: version,
})

const setDates = dates => ({
  type: SET_DCF_DATES,
  payload: dates,
})

const updateAnswer = (questionId, value, field = 'correctedAnswer', meta = null) => ({
  type: UPDATE_DCF_ANSWER,
  payload: { questionId, value, field, meta },
})

const addAnswerRow = () => ({
  type: ADD_DCF_ANSWER_ROW,
})

const removeAnswerRow = questionId => ({
  type: REMOVE_DCF_ANSWER_ROW,
  payload: questionId,
})

const formatParticipantOptions = (participantsPayload = {}) => {
  return Object.keys(participantsPayload).map(ptpId => {
    const ptp = participantsPayload[ptpId] || {}
    const { fname, lname, subject_id: subjectId, site_id: siteId } = ptp
    const nameParts = [fname, lname].filter(Boolean).join(' ')
    const label = nameParts || subjectId || `Participant ${ptpId}`
    const text = subjectId && nameParts ? `${label} (${subjectId})` : label

    return {
      key: String(ptpId),
      text,
      siteId,
      subjectId,
      fname,
      lname,
    }
  })
}

const formatInstrumentOptions = (instruments = []) => {
  return instruments
    .filter(item => item.type !== INSTRUMENT_TYPE_MAP.announcement)
    .map(item => {
      const version = item.metadata?.version ?? item.version ?? item.instrument_version ?? null
      return {
        key: String(item.id),
        text: getInstrumentDisplayName(item),
        type: item.type,
        version: version != null ? String(version) : '',
      }
    })
}

const formatInstanceCandidates = (instances = []) => {
  return instances.map(item => {
    const id = String(item.instance_id || item.id)
    return {
      id,
      startedBy: item.started_by ?? null,
      completedBy: item.completed_by ?? null,
      status: item.status ?? null,
      payload: item,
    }
  })
}

const fetchParticipantsForDcf = studyID => {
  return dispatch => {
    return dispatch(_fetchParticipants(studyID)).then(payload => {
      dispatch(setParticipants(formatParticipantOptions(payload)))
      return payload
    })
  }
}

const fetchInstrumentsForDcf = studyID => {
  return (dispatch, getState) => {
    const { study, location } = getState()
    const hasStudyPromotion = !!study?.currentStudy?.config?.study_promotion
    const studyVersionID = getStudyVersionID(location)
    const query = hasStudyPromotion ? `?study_version_id=${studyVersionID}` : ''

    return dispatch(
      request({
        method: 'GET',
        url: `/control/studies/${studyID}/instruments${query}`,
        success: payload => {
          const results = payload?.results || []
          dispatch(setInstruments(formatInstrumentOptions(results)))
          return results
        },
        hasLoader: true,
        forceLoader: true,
      }),
    )
  }
}

const fetchInstances = (studyID, participantId, instrumentId) => {
  return dispatch => {
    dispatch(setInstances([]))
    dispatch(setSelectedInstance(null))
    dispatch(setAnswers([]))

    return dispatch(
      request({
        method: 'GET',
        url:
          `/dcf/studies/${studyID}/participants/${participantId}/instruments/` +
          `${instrumentId}/instances`,
        errorMessage: 'Error loading instrument instances',
        success: payload => {
          const instances = payload?.instances || payload?.results || []
          dispatch(setInstances(formatInstanceCandidates(instances)))
          return instances
        },
        hasLoader: true,
        forceLoader: true,
      }),
    )
  }
}

/** Survey definition lookup; answers from the parse DB carry no type/choice info. */
const fetchSurveyQuestionMap = (studyID, instrumentId) => {
  return dispatch => {
    if (!studyID || !instrumentId) return Promise.resolve({})
    return dispatch(
      request({
        method: 'GET',
        url: `/control/admin/studies/${studyID}/instruments/${instrumentId}/survey.json`,
        success: payload => buildSurveyQuestionMap(payload || {}),
        fail: () => ({}),
        hasLoader: false,
      }),
    )
  }
}

const fetchAnswers = (studyID, participantId, instrumentId, instanceId) => {
  return dispatch => {
    const answersUrl =
      `/dcf/studies/${studyID}/participants/${participantId}/instruments/` +
      `${instrumentId}/instances/${instanceId}/answers`
    // Cover survey metadata + answers with one forced loader so Next never looks idle.
    dispatch(loadingActions.startLoader(true))
    return dispatch(fetchSurveyQuestionMap(studyID, instrumentId))
      .then(surveyMap => {
        const questionMetaById = surveyMap && typeof surveyMap === 'object' ? surveyMap : {}
        return dispatch(
          request({
            method: 'GET',
            url: answersUrl,
            errorMessage: 'Error fetching instance answers',
            success: payload => {
              const answers = (payload?.answers || payload?.results || []).map(answer => {
                const questionId = String(answer.question_id || answer.id)
                const title =
                  answer.question_title || answer.question_label || answer.label || questionId
                const answerId = answer.answer_id != null ? String(answer.answer_id) : null
                const answerValue = answer.original_answer ?? answer.answer ?? ''
                return withSurveyQuestionMeta(
                  {
                    questionId,
                    questionNumber: answer.question_number ?? null,
                    questionLabel: title,
                    questionTitle: title,
                    questionType: answer.question_type || answer.type || 'text',
                    choices: [],
                    inputValidation: {},
                    originalAnswerId: answerId,
                    answerId,
                    originalAnswer: answerValue,
                    correctedAnswer: answerValue,
                  },
                  questionMetaById[questionId],
                )
              })
              dispatch(setAnswers(answers))
              return answers
            },
            hasLoader: false,
          }),
        )
      })
      .finally(() => {
        dispatch(loadingActions.stopLoader(true))
      })
  }
}

const fetchSurveyQuestions = (studyID, instrumentId) => {
  return dispatch => {
    return dispatch(
      request({
        method: 'GET',
        url: `/control/admin/studies/${studyID}/instruments/${instrumentId}/survey.json`,
        errorMessage: 'Error fetching survey questions',
        success: payload => {
          const answers = formatSurveyQuestions(payload)
          dispatch(setAnswers(answers))
          return answers
        },
        fail: () => false,
        hasLoader: true,
        forceLoader: true,
      }),
    )
  }
}

const toIsoDateTime = value => {
  if (!value) return null
  if (/^\d{4}-\d{2}-\d{2}$/.test(value)) {
    return `${value}T00:00:00`
  }
  return value
}

const buildDcfPayload = (state, { asDraft = false } = {}) => {
  const {
    selectedParticipant,
    selectedInstrument,
    selectedInstance,
    selectedAction,
    instrumentVersion,
    startedBy,
    completedBy,
    answers,
  } = state

  const action = selectedAction || 'add_data_to_existing_record'
  const needsVersion = action === 'create_new_record' || action === 'add_new_record'

  const payload = {
    participant_id: selectedParticipant?.key,
    // Stored in S3 answers.json for list/review display (subject id / instrument name).
    subject_id: selectedParticipant?.subjectId || null,
    instrument_id: selectedInstrument?.key,
    instrument_name: selectedInstrument?.text || null,
    action,
    status: asDraft ? 'in_draft' : 'submitted',
    // Instance is associated later from the DCF list (all actions).
    instance_id: selectedInstance?.id || selectedInstance || null,
    start_by: toIsoDateTime(startedBy),
    completed_by: toIsoDateTime(completedBy),
    answers: (answers || [])
      .filter(answer => {
        if (action === 'flag_questions_only') return false
        const qid = answer.isManual ? answer.questionIdEditable : answer.questionId
        if (!qid) return false
        // Keep unchanged rows for review/audit; apply uses original → corrected.
        return answer.correctedAnswer != null
      })
      .map(answer => {
        const qid = answer.isManual
          ? String(answer.questionIdEditable || '').trim()
          : answer.questionId
        return {
          question_id: qid,
          question_number:
            answer.questionNumber != null && answer.questionNumber !== ''
              ? Number(answer.questionNumber)
              : null,
          question_label: answer.questionTitle || answer.questionLabel || null,
          original_answer_id: answer.originalAnswerId ?? null,
          answer_id: answer.answerId ?? null,
          original_answer: answer.originalAnswer ?? null,
          corrected_answer: answer.correctedAnswer,
        }
      }),
  }

  if (needsVersion || instrumentVersion) {
    payload.instrument_version = instrumentVersion || selectedInstrument?.version || null
  }

  return payload
}

const submitDcf = (studyID, studyVersionID, { asDraft = false } = {}) => {
  return (dispatch, getState) => {
    const { createDcf } = getState()
    const body = buildDcfPayload(createDcf, { asDraft })

    if (!body.action) {
      dispatch(
        notyActions.showError({
          text: generateNotyMessage('Please select a DCF action.', false),
        }),
      )
      return Promise.resolve()
    }

    if (
      (body.action === 'create_new_record' || body.action === 'add_new_record') &&
      !body.instrument_version
    ) {
      dispatch(
        notyActions.showError({
          text: generateNotyMessage('Instrument version is required for this action.', false),
        }),
      )
      return Promise.resolve()
    }

    const successMessage = asDraft
      ? 'DCF saved as draft.'
      : 'Data correction request submitted successfully.'
    const errorMessage = asDraft
      ? 'There was an error saving the draft. Please try again.'
      : 'There was an error submitting the DCF. Please try again.'

    const success = () => {
      dispatch(
        notyActions.showSuccess({
          text: generateNotyMessage(successMessage),
        }),
      )
      dispatch(resetCreateDcf())
      browserHistory.push(`/studies/${studyID}/${studyVersionID}/data-correction-v2`)
    }

    const fail = () => {
      dispatch(
        notyActions.showError({
          text: generateNotyMessage(errorMessage, false),
        }),
      )
    }

    return dispatch(
      request({
        method: 'POST',
        url: `/dcf/studies/${studyID}/datacorrection`,
        body: JSON.stringify(body),
        success,
        fail,
        hasLoader: true,
        forceLoader: true,
      }),
    )
  }
}

const _defaultState = {
  step: 1,
  participants: [],
  instruments: [],
  instances: [],
  answers: [],
  selectedParticipant: null,
  selectedInstrument: null,
  selectedInstance: null,
  selectedAction: 'add_data_to_existing_record',
  instrumentVersion: '',
  startedBy: '',
  completedBy: '',
}

const CreateDcf = (state = _defaultState, action) => {
  switch (action.type) {
    case RESET_CREATE_DCF:
      return _defaultState
    case SET_DCF_PARTICIPANTS:
      return { ...state, participants: action.payload }
    case SET_DCF_INSTRUMENTS:
      return { ...state, instruments: action.payload }
    case SET_DCF_INSTANCES:
      return { ...state, instances: action.payload }
    case SET_DCF_ANSWERS:
      return { ...state, answers: action.payload }
    case SET_CREATE_DCF_STEP:
      return { ...state, step: action.payload }
    case SET_SELECTED_PARTICIPANT:
      return {
        ...state,
        selectedParticipant: action.payload,
        selectedInstance: null,
        instances: [],
        answers: [],
        startedBy: '',
        completedBy: '',
      }
    case SET_SELECTED_INSTRUMENT:
      return {
        ...state,
        selectedInstrument: action.payload,
        selectedInstance: null,
        instances: [],
        answers: [],
        startedBy: '',
        completedBy: '',
        instrumentVersion: action.payload?.version || state.instrumentVersion || '',
      }
    case SET_SELECTED_INSTANCE:
      return { ...state, selectedInstance: action.payload, answers: [] }
    case SET_SELECTED_ACTION:
      return {
        ...state,
        selectedAction: action.payload,
        selectedInstance: action.payload === 'create_new_record' ? null : state.selectedInstance,
        instances: action.payload === 'create_new_record' ? [] : state.instances,
        answers: action.payload === 'create_new_record' ? [] : state.answers,
      }
    case SET_INSTRUMENT_VERSION:
      return { ...state, instrumentVersion: action.payload }
    case SET_DCF_DATES:
      return {
        ...state,
        startedBy: action.payload.startedBy ?? state.startedBy,
        completedBy: action.payload.completedBy ?? state.completedBy,
      }
    case UPDATE_DCF_ANSWER: {
      const { questionId, value, field, meta } = action.payload
      return {
        ...state,
        answers: state.answers.map(answer => {
          if (answer.questionId !== questionId) return answer
          if (field === 'questionIdEditable') return { ...answer, questionIdEditable: value }
          if (field === 'questionNumber') return { ...answer, questionNumber: value }
          if (field === 'questionTitle') {
            return { ...answer, questionTitle: value, questionLabel: value }
          }
          const answerId =
            meta && Object.prototype.hasOwnProperty.call(meta, 'answerId')
              ? meta.answerId
              : answer.answerId
          if (answer.isSurveyAnswer) {
            return {
              ...answer,
              originalAnswer: value,
              correctedAnswer: value,
              originalAnswerId: answerId,
              answerId,
            }
          }
          return { ...answer, correctedAnswer: value, answerId }
        }),
      }
    }
    case ADD_DCF_ANSWER_ROW: {
      const id = `manual_${Date.now()}`
      return {
        ...state,
        answers: [
          ...state.answers,
          {
            questionId: id,
            questionIdEditable: '',
            questionNumber: '',
            questionLabel: '',
            questionTitle: '',
            questionType: 'text',
            originalAnswerId: null,
            answerId: null,
            originalAnswer: '',
            correctedAnswer: '',
            isManual: true,
          },
        ],
      }
    }
    case REMOVE_DCF_ANSWER_ROW:
      return {
        ...state,
        answers: state.answers.filter(answer => answer.questionId !== action.payload),
      }
    default:
      return state
  }
}

export default CreateDcf

export const actions = {
  resetCreateDcf,
  setParticipants,
  setInstruments,
  setInstances,
  setAnswers,
  setStep,
  setSelectedParticipant,
  setSelectedInstrument,
  setSelectedInstance,
  setSelectedAction,
  setInstrumentVersion,
  setDates,
  updateAnswer,
  addAnswerRow,
  removeAnswerRow,
  fetchParticipantsForDcf,
  fetchInstrumentsForDcf,
  fetchInstances,
  fetchAnswers,
  fetchSurveyQuestions,
  submitDcf,
  buildDcfPayload,
  DCF_ACTION_OPTIONS,
}
